import * as THREE from "three";
import { entityDefinitions } from "../data/entities";
import type { WorldEntity } from "../data/model";
import { motor as M } from "../data/tuning";
import { CAR_HALF_LENGTH } from "../engine/Collision";

/** The car as the engine sees it (from a RunSession snapshot). */
export interface DebugCar {
  playerX: number;
  playerY: number;
  ducking: boolean;
}

/**
 * Dev overlay (`?boxes`): every collision box near the car as a wireframe drawn over the models — red = solid,
 * green = a top you can drive on, yellow = trigger or pickup, white = the car. What you see vs what you hit:
 * a model that sticks out of its box (or a box with no model) is the "died for no reason" class of bug.
 * Scene convention (GameScene): x is mirrored by `vx`, z is relative to the car, y up from the road.
 */
export class DebugBoxes {
  private readonly group = new THREE.Group();
  private readonly pool: THREE.LineSegments[] = [];
  private readonly edges = new THREE.EdgesGeometry(new THREE.BoxGeometry(1, 1, 1));
  private readonly mats = {
    solid: this.line(0xff2a2a),
    surface: this.line(0x18ff7a),
    trigger: this.line(0xffd400),
    car: this.line(0xffffff)
  };

  constructor(scene: THREE.Scene) {
    this.group.name = "debug-boxes";
    scene.add(this.group);
  }

  update(entities: WorldEntity[], carZ: number, car: DebugCar, vx: number): void {
    let used = 0;
    const take = (material: THREE.LineBasicMaterial): THREE.LineSegments => {
      let line = this.pool[used];
      if (!line) {
        line = new THREE.LineSegments(this.edges, material);
        line.renderOrder = 999;
        line.frustumCulled = false;
        this.pool.push(line);
        this.group.add(line);
      }
      line.material = material;
      line.visible = true;
      used += 1;
      return line;
    };
    for (const e of entities) {
      if (!e.active) continue;
      const rel = e.z - carZ;
      if (rel + e.depth < -20 || rel > 180) continue;
      const def = entityDefinitions[e.kind];
      const material = def.trigger ? this.mats.trigger : def.surface === "roof" || def.surface === "ground" ? this.mats.surface : this.mats.solid;
      const box = take(material);
      box.scale.set(Math.max(0.05, e.width), Math.max(0.05, e.height), Math.max(0.05, e.depth));
      box.position.set(vx * e.x, e.yBase + e.height / 2, rel + e.depth / 2);
    }
    const height = M.colliderHeight * (car.ducking ? M.rollHeightFactor : 1);
    const self = take(this.mats.car);
    self.scale.set(M.colliderHalfWidth * 2, height, CAR_HALF_LENGTH * 2);
    self.position.set(vx * car.playerX, car.playerY + height / 2, 0);
    for (let i = used; i < this.pool.length; i++) this.pool[i].visible = false;
  }

  dispose(): void {
    this.group.removeFromParent();
    this.edges.dispose();
    for (const m of Object.values(this.mats)) m.dispose();
  }

  private line(color: number): THREE.LineBasicMaterial {
    return new THREE.LineBasicMaterial({ color, depthTest: false, transparent: true, opacity: 0.95, toneMapped: false });
  }
}
